import * as React from 'react'
import { data, redirect, Form, Link, useActionData } from 'react-router'
import { z } from 'zod'
import { type Route } from './+types/register.ts'

const vendorRegistrationSchema = z.object({
	businessName: z.string().trim().min(1, 'Business name is required'),
	contactName: z.string().trim().min(1, 'Contact name is required'),
	email: z.string().trim().email('Please enter a valid email'),
	phone: z.string().trim().min(1, 'Phone is required'),
	address: z.string().trim().min(1, 'Address is required'),
	city: z.string().trim().min(1, 'City is required'),
	state: z.string().trim().min(1, 'State is required'),
	zip: z.string().trim().min(1, 'Zip is required'),
	sponsorLevel: z.enum(
		[
			'Platinum',
			'Gold',
			'Silver',
			'Large Trophy',
			'Small Trophy',
			'Vendor Spot',
			'Other',
		],
		{
			errorMap: () => ({
				message: 'We need to know the sponsor level.',
			}),
		},
	),
	terms: z.preprocess(
		(arg) => {
			if (typeof arg === 'string') return arg === 'on'
			return false
		},
		z.boolean().refine((v) => v, {
			message: 'You must agree to the terms and conditions',
		}),
	),
})

export const meta: Route.MetaFunction = () => [
	{ title: 'EBHS Events - 2025 Car Show and Parade' },
]

export async function action({ request }: Route.ActionArgs) {
	const { createVendorRegistration } = await import(
		'#app/models/vendor-registration.server.ts'
	)
	const formData = await request.formData()
	const result = vendorRegistrationSchema.safeParse(
		Object.fromEntries(formData),
	)

	if (!result.success) {
		return data(
			{
				errors: result.error.flatten().fieldErrors,
				fields: Object.fromEntries(formData) as Record<string, string>,
			},
			{ status: 400 },
		)
	}

	const { terms: _, ...registrationData } = result.data
	await createVendorRegistration(2025, {
		...registrationData,
		paymentAmount: '',
	})

	return redirect('/events/2025-car-show-vendor/registration-confirmation')
}

export default function VendorRegister() {
	const actionData = useActionData<typeof action>()

	const emailRef = React.useRef<HTMLInputElement>(null)
	const termsRef = React.useRef<HTMLInputElement>(null)

	const [sponsorLevel, setSponsorLevel] = React.useState<string>(
		actionData?.fields?.sponsorLevel || 'unknown',
	)

	React.useEffect(() => {
		if (actionData?.errors?.email) {
			emailRef.current?.focus()
		} else if (actionData?.errors?.terms) {
			termsRef.current?.focus()
		}
	}, [actionData])

	return (
		<>
			<header className="py-5">
				<div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
					<h2 className="text-3xl font-bold leading-tight text-gray-900">
						2025 Car Show Vendor/Trophy Registration
					</h2>
					<p className="mt-3">
						If you are registering multiple vendors, please complete this
						form for each vendor being entered. If you prefer to register
						by mail, you can download a{' '}
						<a
							href="https://ebhs1838.s3.amazonaws.com/events/2025-car-show-vendor-registration.pdf"
							target="_blank"
							className="whitespace-nowrap font-medium text-ebhs-blue underline hover:text-ebhs-blue-dark"
							rel="noreferrer"
						>
							registration form
						</a>{' '}
						and mail it in. Visit{' '}
						<Link
							to="/events/2025-car-show-and-parade"
							className="text-ebhs-blue underline hover:text-ebhs-blue-dark"
						>
							the event page
						</Link>{' '}
						for more details.
					</p>
				</div>
			</header>
			<div className="px-4 pt-4 pb-8">
				<div className="mx-auto max-w-6xl text-lg sm:px-6 lg:px-8">
					<Form method="post" className="space-y-4">
						<div>
							<label
								htmlFor="businessName"
								className="block text-sm font-medium text-gray-700"
							>
								Business Name
							</label>
							<input
								type="text"
								id="businessName"
								name="businessName"
								required
								defaultValue={actionData?.fields?.businessName}
								className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
							/>
							{actionData?.errors?.businessName ? (
								<div className="pt-1 text-red-700">
									{actionData.errors.businessName}
								</div>
							) : null}
						</div>

						<div>
							<label
								htmlFor="contactName"
								className="block text-sm font-medium text-gray-700"
							>
								Contact Name
							</label>
							<input
								type="text"
								id="contactName"
								name="contactName"
								required
								defaultValue={actionData?.fields?.contactName}
								className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
							/>
							{actionData?.errors?.contactName ? (
								<div className="pt-1 text-red-700">
									{actionData.errors.contactName}
								</div>
							) : null}
						</div>

						<div>
							<label
								htmlFor="email"
								className="block text-sm font-medium text-gray-700"
							>
								Email
							</label>
							<input
								type="email"
								id="email"
								name="email"
								ref={emailRef}
								required
								defaultValue={actionData?.fields?.email}
								className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
							/>
							{actionData?.errors?.email ? (
								<div className="pt-1 text-red-700">
									{actionData.errors.email}
								</div>
							) : null}
						</div>

						<div>
							<label
								htmlFor="phone"
								className="block text-sm font-medium text-gray-700"
							>
								Phone
							</label>
							<input
								type="text"
								id="phone"
								name="phone"
								required
								defaultValue={actionData?.fields?.phone}
								className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
							/>
							{actionData?.errors?.phone ? (
								<div className="pt-1 text-red-700">
									{actionData.errors.phone}
								</div>
							) : null}
						</div>

						<div>
							<label
								htmlFor="address"
								className="block text-sm font-medium text-gray-700"
							>
								Address
							</label>
							<input
								type="text"
								id="address"
								name="address"
								required
								defaultValue={actionData?.fields?.address}
								className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
							/>
							{actionData?.errors?.address ? (
								<div className="pt-1 text-red-700">
									{actionData.errors.address}
								</div>
							) : null}
						</div>

						<div>
							<label
								htmlFor="city"
								className="block text-sm font-medium text-gray-700"
							>
								City
							</label>
							<input
								type="text"
								id="city"
								name="city"
								required
								defaultValue={actionData?.fields?.city}
								className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
							/>
							{actionData?.errors?.city ? (
								<div className="pt-1 text-red-700">
									{actionData.errors.city}
								</div>
							) : null}
						</div>

						<div>
							<label
								htmlFor="state"
								className="block text-sm font-medium text-gray-700"
							>
								State
							</label>
							<input
								type="text"
								id="state"
								name="state"
								required
								defaultValue={actionData?.fields?.state}
								className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
							/>
							{actionData?.errors?.state ? (
								<div className="pt-1 text-red-700">
									{actionData.errors.state}
								</div>
							) : null}
						</div>

						<div>
							<label
								htmlFor="zip"
								className="block text-sm font-medium text-gray-700"
							>
								Zip
							</label>
							<input
								type="text"
								id="zip"
								name="zip"
								required
								defaultValue={actionData?.fields?.zip}
								className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
							/>
							{actionData?.errors?.zip ? (
								<div className="pt-1 text-red-700">
									{actionData.errors.zip}
								</div>
							) : null}
						</div>

						<div>
							<label
								htmlFor="sponsorLevel"
								className="block text-sm font-medium text-gray-700"
							>
								What sponsorship level are you interested in?
							</label>
							<select
								id="sponsorLevel"
								name="sponsorLevel"
								value={sponsorLevel}
								onChange={(e) => setSponsorLevel(e.target.value)}
								className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
							>
								<option value="unknown">
									Please select an option
								</option>
								<option value="Platinum">Platinum ($2000)</option>
								<option value="Gold">Gold ($1000)</option>
								<option value="Silver">Silver ($500)</option>
								<option value="Large Trophy">
									Large Trophy ($100)
								</option>
								<option value="Small Trophy">
									Small Trophy ($50)
								</option>
								<option value="Vendor Spot">
									Vendor Spot ($30)
								</option>
								<option value="Other">Other</option>
							</select>
							{actionData?.errors?.sponsorLevel ? (
								<div className="pt-1 text-red-700">
									{actionData.errors.sponsorLevel}
								</div>
							) : null}
						</div>

						<p>
							<input
								type="checkbox"
								name="terms"
								ref={termsRef}
								required
							/>{' '}
							I agree to release East Bloomfield Historical Society and any
							of their sponsors of all liability and damage from any cause
							whatsoever.
							{actionData?.errors?.terms ? (
								<div className="pt-1 text-red-700">
									{actionData.errors.terms}
								</div>
							) : null}
						</p>
						<button className="rounded bg-blue-500 py-2 px-4 text-white hover:bg-blue-600 focus:bg-blue-400">
							Register
						</button>
					</Form>
				</div>
			</div>
		</>
	)
}
